/**
 * Settings Layout Component
 * 
 * Provides a secondary sidebar navigation within the Settings section.
 * Links directly to existing settings pages and modals.
 * 
 * Sections:
 * - General (Workspace/Tenant settings)
 * - Preferences (User timezone, theme, etc.)
 * - Models & Providers (tenant-scoped model management)
 * - Your Providers / BYOM (tenant custom providers)
 * - Platform Providers (system tenant admin only)
 * - IAM / Permissions (admin only — roles, modules, users & groups)
 * - Storage (admin only)
 */

import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { useHasRole as useTenantRole } from '@/hooks/useRoles';
import { useHasRole as useAuthRole } from '@/lib/auth';
import { useIsSystemAdmin } from '@/components/SystemAdminGuard';
import { useTenantAccess } from '@/features/permissions/hooks/usePermissions';
import { ErrorBoundary } from 'shared/components/errors';
import {
  Settings,
  User,
  Cpu,
  Shield,
  ShieldAlert,
  Server,
  Key,
  HardDrive,
  BookOpen,
  MessageSquare,
  Plug,
  Sparkles,
  Globe,
  Library,
  Activity,
} from 'lucide-react';

// =============================================================================
// Navigation Items
// =============================================================================

interface NavItem {
  to: string;
  labelKey: string;
  icon: React.ElementType;
  /** Only show to admins */
  adminOnly?: boolean;
  /** Only show to system tenant admins */
  systemOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  {
    to: '/settings',
    labelKey: 'admin.settings.nav.general',
    icon: Settings,
  },
  {
    to: '/settings/preferences',
    labelKey: 'admin.settings.nav.preferences',
    icon: User,
  },
  {
    to: '/settings/models',
    labelKey: 'admin.settings.nav.modelsProviders',
    icon: Cpu,
  },
  {
    to: '/settings/providers',
    labelKey: 'admin.settings.nav.yourProviders',
    icon: Key,
  },
  {
    to: '/settings/platform-providers',
    labelKey: 'admin.settings.nav.platformProviders',
    icon: Server,
    systemOnly: true,
  },
  {
    to: '/settings/llm-catalog',
    labelKey: 'admin.settings.nav.llmCatalog',
    icon: Library,
    systemOnly: true,
  },
  {
    to: '/settings/dr-llm-policy',
    labelKey: 'admin.settings.nav.drLlmPolicy',
    icon: Globe,
    systemOnly: true,
  },
  {
    to: '/settings/web-capability',
    labelKey: 'admin.settings.nav.webCapability',
    icon: Globe,
    systemOnly: true,
  },
  {
    to: '/settings/llm-policy',
    labelKey: 'admin.settings.nav.llmPolicy',
    icon: Sparkles,
    adminOnly: true,
  },
  {
    to: '/settings/guardrails',
    labelKey: 'admin.settings.nav.guardrails',
    icon: ShieldAlert,
    adminOnly: true,
  },
  {
    to: '/settings/knowledge',
    labelKey: 'admin.settings.nav.knowledge',
    icon: BookOpen,
    adminOnly: true,
  },
  {
    to: '/settings/chat',
    labelKey: 'admin.settings.nav.chat',
    icon: MessageSquare,
    adminOnly: true,
  },
  {
    to: '/settings/permissions',
    labelKey: 'admin.settings.nav.iam',
    icon: Shield,
    adminOnly: true,
  },
  {
    to: '/settings/integrations',
    labelKey: 'admin.settings.nav.integrations',
    icon: Plug,
    adminOnly: true,
  },
  {
    to: '/settings/storage',
    labelKey: 'admin.settings.nav.storage',
    icon: HardDrive,
    adminOnly: true,
  },
  {
    to: '/settings/telemetry',
    labelKey: 'admin.settings.nav.telemetry',
    icon: Activity,
    adminOnly: true,
  },
];

// =============================================================================
// Settings Layout
// =============================================================================

export function SettingsLayout() {
  const { t } = useI18n();
  // ISS-474: the authoritative admin gate is the Keto-resolved tenant role
  // (SSoT). Session/context roles can go stale during tenant switching and
  // hide IAM from tenant admins/owners. Fail-closed: once the Keto check
  // resolves, it wins; while loading, the legacy checks provide a first
  // paint (backend endpoints still enforce permissions).
  const tenantAccess = useTenantAccess();
  const isAdmin = tenantAccess.isSuccess
    ? Boolean(tenantAccess.data?.is_tenant_admin)
    : useTenantRole('admin') || useAuthRole('admin');
  const isSystemTenantAdmin = useIsSystemAdmin();
  const location = useLocation();
  const navigate = useNavigate();

  const visibleItems = NAV_ITEMS.filter((item) => {
    if (item.systemOnly && !isSystemTenantAdmin) return false;
    if (item.adminOnly && !isAdmin) return false;
    return true;
  });

  const activeItem = visibleItems.find((item) =>
    item.to === '/settings'
      ? location.pathname === '/settings'
      : location.pathname.startsWith(item.to),
  );

  return (
    <div className="flex h-full flex-col lg:flex-row">
      {/* Mobile section selector (ADR-114: no fixed sidebar on small screens) */}
      <div className="border-b border-gray-200 bg-gray-50 p-3 lg:hidden dark:border-gray-700 dark:bg-gray-900/50">
        <label htmlFor="settings-section" className="sr-only">
          {t('admin.settings.title') || 'Settings'}
        </label>
        <select
          id="settings-section"
          value={activeItem?.to ?? '/settings'}
          onChange={(event) => navigate(event.target.value)}
          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        >
          {visibleItems.map((item) => (
            <option key={item.to} value={item.to}>
              {t(item.labelKey)}
            </option>
          ))}
        </select>
      </div>

      {/* Settings Sidebar (desktop) */}
      <aside className="hidden w-56 shrink-0 border-r border-gray-200 bg-gray-50 lg:block dark:border-gray-700 dark:bg-gray-900/50">
        <div className="p-4">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
            {t('admin.settings.title') || 'Settings'}
          </h2>
          <nav className="space-y-1">
            {visibleItems.map((item) => {
              const Icon = item.icon;
              // For the index route, only match exact path
              const isActive = item.to === '/settings'
                ? location.pathname === '/settings'
                : location.pathname.startsWith(item.to);
              
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/settings'}
                  className={`
                    flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors
                    ${isActive
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }
                  `}
                >
                  <Icon size={16} className={isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'} />
                  {t(item.labelKey)}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Content Area */}
      <main className="flex-1 overflow-y-auto">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  );
}
